import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule, NavigationStart } from '@angular/router';
import { InputTextModule } from 'primeng/inputtext';
import { OverlayModule } from 'primeng/overlay';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { HighlightPipe } from '../../../../shared/pipe/highlight.pipe';
import { LookupService } from '../../../../services/lookup.service';
import { Subject, takeUntil } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { SearchResult } from '../../../../api/search/search-result';
import { toPTDateString } from '../../../../shared/utils';

@Component({
  selector: 'app-header-search',
  standalone: true,
  imports: [CommonModule, FormsModule, InputTextModule, OverlayModule, RouterModule, ProgressSpinnerModule, HighlightPipe],
  providers: [LookupService],
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss'],
})
export class SearchComponent implements OnDestroy, OnInit {
  toPTDateString = toPTDateString;
  searchText: string = '';
  searchResult: SearchResult | null = null;
  showResults: boolean = false;
  loading: boolean = false;

  private readonly searchAbort$ = new Subject<void>();
  private readonly destroy$ = new Subject<void>();

  private readonly searchText$ = new Subject<string>();

  get hasResults(): boolean {
    if (!this.searchResult) return false;

    const hasCompanies = this.searchResult.companies && this.searchResult.companies.length > 0;
    const hasMatters = this.searchResult.matters && this.searchResult.matters.length > 0;
    const hasForeignNationals = this.searchResult.foreignNationals && this.searchResult.foreignNationals.length > 0;
    const hasContacts = this.searchResult.contacts && this.searchResult.contacts.length > 0;
    const hasLocations = this.searchResult.locations && this.searchResult.locations.length > 0;

    return hasCompanies || hasMatters || hasForeignNationals || hasContacts || hasLocations;
  }

  constructor(
    private readonly lookupService: LookupService,
    private readonly router: Router
  ) { }

  ngOnInit(): void {
    this.searchText$
      .pipe(
        debounceTime(500),
        takeUntil(this.destroy$)
      )
      .subscribe(searchTextValue => {
        this.loading = true;
        this.performSearch(searchTextValue)
          .pipe(takeUntil(this.searchAbort$))
          .subscribe({
            next: (result: SearchResult) => {
              this.searchResult = result;

              this.showResults = true;
              this.loading = false;
            },
            error: () => {
              this.loading = false;
            }
          });
      });

    // Clear search when navigating to other pages
    this.router.events
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => {
        if (event instanceof NavigationStart) {
          this.cancelSearch();
          this.clearSearch();
        }
      });
  }

  ngOnDestroy() {
    this.searchAbort$.next();
    this.searchAbort$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  onResultItemClick() {
    this.clearSearch();
  }

  onSearch() {
    this.cancelSearch();
    if (this.searchText.length >= 2) {
      this.searchText$.next(this.searchText);
    }
  }

  joinValues(...values: (string | undefined | null)[]): string {
    return values.filter((value) => value?.trim()).join(' | ');
  }

  private performSearch(text: string) {
    return this.lookupService.search({
      filter: text,
      pageSizeByType: 3,
      pageIndex: 0,
      entityType: null,
      getPageCount: false,
    });
  }

  private cancelSearch(): void {
    this.searchAbort$.next();
    this.loading = false;
  }

  private clearSearch(): void {
    this.searchText = '';
    this.searchResult = null;
    this.showResults = false;
  }
}
